import { useMemo } from "react";
import LazyApexChart from "../../../client/components/LazyApexChart";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtMoney(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`;
}

export default function DayOfWeekWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "day", label: t.analytics.dayLabel, align: "left" as const },
    { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
    { key: "count", label: t.analytics.numberOfTrades, align: "right" as const },
  ];
  const tableRows = (analyticsData?.dayOfWeek?.dayStats ?? []).map((d) => ({
    day: d.day,
    pnl: (
      <span className={d.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
        {d.pnl >= 0 ? "+" : ""}
        {d.pnl.toFixed(2)}
      </span>
    ),
    count: d.count,
  }));

  const chartOptions = useMemo(() => {
    if (!analyticsData || analyticsData.dayOfWeek.dayStats.length === 0) return null;
    const { dayStats } = analyticsData.dayOfWeek;
    const c = chartColors();
    return {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
      },
      plotOptions: {
        bar: { borderRadius: 3, barHeight: "60%", horizontal: true, distributed: true },
      },
      xaxis: {
        categories: dayStats.map((d) => d.dayShort),
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: string | number) =>
            `${Number(v) >= 0 ? "+" : ""}${Number(v).toFixed(0)}$`,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          formatter: (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)} $`,
        },
      },
      colors: dayStats.map((d) => pnlColor(d.avgPnl)),
      legend: { show: false },
      dataLabels: { enabled: false },
    };
  }, [analyticsData, isDark]);

  if (!analyticsData || analyticsData.dayOfWeek.dayStats.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.dayOfWeek} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  const { dayStats, bestDay, worstDay } = analyticsData.dayOfWeek;

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.dayOfWeek}
        isDark={isDark}
        compact
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 flex flex-col gap-2 px-4 pb-4 pt-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {t.analytics.dayOfWeekPerformance} · {t.analytics.averagePerTrade}
        </p>
        {viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
        ) : chartOptions ? (
          <LazyApexChart
            key={`day-of-week-widget-${themeId}`}
            options={chartOptions}
            series={[
              {
                name: t.analytics.averagePerTrade,
                data: dayStats.map((d) => Math.round(d.avgPnl * 100) / 100),
              },
            ]}
            type="bar"
            height={200}
          />
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="rounded-lg bg-muted/40 p-2">
            <p className="text-[9px] uppercase tracking-wide text-muted-foreground">
              {t.analytics.best}
            </p>
            <p className="font-mono text-sm font-bold">
              {bestDay ? bestDay.dayShort : "—"}{" "}
              {bestDay && (
                <span style={{ color: pnlColor(bestDay.avgPnl) }}>
                  {fmtMoney(bestDay.avgPnl)}
                </span>
              )}
            </p>
          </div>
          <div className="rounded-lg bg-muted/40 p-2">
            <p className="text-[9px] uppercase tracking-wide text-muted-foreground">
              {t.analytics.worst}
            </p>
            <p className="font-mono text-sm font-bold">
              {worstDay ? worstDay.dayShort : "—"}{" "}
              {worstDay && (
                <span style={{ color: pnlColor(worstDay.avgPnl) }}>
                  {fmtMoney(worstDay.avgPnl)}
                </span>
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
